<template>
  <div class="min-h-screen bg-gray-50">
    <div v-if="event" class="max-w-6xl mx-auto">
      <!-- Hero Section -->
      <div class="relative">
        <!-- Event Image -->
        <div class="h-64 md:h-96 lg:h-[500px] overflow-hidden">
          <img
            v-if="validImageUrl"
            :src="validImageUrl"
            :alt="event.title"
            class="w-full h-full object-cover"
            @error="handleImageError"
            @load="handleImageLoad"
          />
          <div v-else class="w-full h-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <span class="text-white text-6xl md:text-8xl">🎪</span>
          </div>
        </div>
        
        <!-- Overlay with event info -->
        <div class="absolute inset-0 bg-black bg-opacity-40 flex items-end">
          <div class="w-full p-6 md:p-8 text-white">
            <h1 class="text-3xl md:text-5xl lg:text-6xl font-bold mb-4 drop-shadow-lg">{{ event.title }}</h1>
            <div class="flex flex-col md:flex-row md:items-center gap-4 text-lg md:text-xl">
              <div class="flex items-center gap-2">
                <span class="text-2xl">📅</span>
                <span>{{ formatDate(event.date) }}</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-2xl">📍</span>
                <span>{{ event.location }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Back Button (under the image) -->
      <div class="p-4 md:p-8">
        <button 
          @click="goBack"
          class="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
          </svg>
          Tillbaka
        </button>
      </div>

      <!-- Content Section -->
      <div class="p-4 md:p-8 lg:p-12">
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <!-- Main Content -->
          <div class="lg:col-span-2">
            <div class="bg-white rounded-lg shadow-lg p-6 md:p-8">
              <h2 class="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Om evenemanget</h2>
              <p class="text-gray-700 text-lg leading-relaxed">{{ event.description }}</p>
            </div>
          </div>

          <!-- Ticket Section -->
          <div class="lg:col-span-1">
            <div class="bg-white rounded-lg shadow-lg p-6 md:p-8">
              <h2 class="text-2xl font-bold text-gray-900 mb-6">Tillgängliga Biljetter</h2>
              
              <div class="space-y-4 mb-8">
                <div
                  v-for="ticket in event.ticketTypes"
                  :key="ticket.name"
                  class="border border-gray-200 rounded-lg p-4 hover:border-indigo-300 transition-colors"
                >
                  <div class="flex justify-between items-start mb-2">
                    <h3 class="text-lg font-semibold text-gray-900">{{ ticket.name }}</h3>
                    <span class="text-2xl font-bold text-indigo-600">{{ ticket.price }} kr</span>
                  </div>
                  <div class="flex justify-between items-center text-sm text-gray-600">
                    <span>Antal kvar:</span>
                    <span class="font-semibold">{{ ticket.quantity }}</span>
                  </div>
                </div>
              </div>

              <!-- Test Ticket Purchase Component -->
              

              <router-link
                :to="`/events/${event._id}/tickets`"
                class="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-lg font-semibold py-4 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
              >
                <span class="text-2xl">🎟️</span>
                Skaffa biljetter
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-else class="min-h-screen flex items-center justify-center">
      <div class="text-center">
        <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto mb-4"></div>
        <p class="text-gray-600 text-lg">Laddar evenemang...</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  components: {
  },
  data() {
    return {
      event: null,
    };
  },
  computed: {
    validImageUrl() {
      if (!this.event?.imageUrl) return null;
      
      // Check if it's a valid data URL
      if (this.event.imageUrl.startsWith('data:image/')) {
        return this.event.imageUrl;
      }
      
      // Check if it's a regular URL
      if (this.event.imageUrl.startsWith('http://') || this.event.imageUrl.startsWith('https://')) {
        return this.event.imageUrl;
      }
      
      // If it doesn't start with data: or http, it might be malformed
      console.warn('Invalid image URL format:', this.event.imageUrl.substring(0, 100));
      return null;
    }
  },
  methods: {
    formatDate(dateStr) {
      return new Date(dateStr).toLocaleString('sv-SE', {
        dateStyle: 'full',
        timeStyle: 'short',
      });
    },
    async buyTicket(ticketName) {
      try {
        await axios.post(
          `/api/events/${this.event._id}/buy`,
          { ticketType: ticketName },
          { withCredentials: true }
        );
        alert('🎫 Biljett köpt!');
      } catch (err) {
        console.error('Köp misslyckades:', err);
        alert('❌ Det gick inte att köpa biljetten.');
      }
    },
    goBack() {
      this.$router.go(-1);
    },
    handleImageError(event) {
      console.error('Image failed to load:', event.target.src);
      // Optionally, you can set a default image or display an error message
      event.target.src = '/assets/default-event-image.jpg'; // Example: set a default image
    },
    handleImageLoad(event) {
      console.log('Image loaded successfully:', event.target.src);
    }
  },
  async created() {
    try {
      const res = await axios.get(`/api/events/${this.$route.params.id}`, {
        withCredentials: true
      });
      this.event = res.data;
      
      // Debug: Log the event data to see what we're getting
      console.log('🔍 Event data received:', {
        id: this.event._id,
        title: this.event.title,
        hasImageUrl: !!this.event.imageUrl,
        imageUrlLength: this.event.imageUrl ? this.event.imageUrl.length : 0,
        imageUrlStart: this.event.imageUrl ? this.event.imageUrl.substring(0, 50) + '...' : 'none'
      });
      
    } catch (err) {
      console.error('Kunde inte hämta event:', err);
    }
  }
};
</script>

<style scoped>
/* Custom styles for better mobile experience */
@media (max-width: 768px) {
  .drop-shadow-lg {
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
  }
}
</style>
